/* eslint-disable no-restricted-globals */
import moment, { Moment } from 'moment';
import 'moment/locale/pt-br';

interface ITransformStringSingularIntoPluralProps {
  data: { name: string }[];
  value: number[];
}

export const DATE_FORMATS = {
  DD_MMM: 'DD MMM',
  YYYY: 'YYYY',
};

const PREPOSITIONS = ['da', 'de', 'di', 'do', 'du'];

// TEXT FORMATTING
export const capitalizeFirstLetter = (text: string, onlyFirst = false) => {
  try {
    if (onlyFirst) {
      return text.charAt(0).toUpperCase().concat(text.slice(1));
    }

    if (text.length > 1) {
      const words = text.split(' ');

      return words
        .map(([first, ...rest]) => {
          const word = String(first + rest);

          const isPreposition = PREPOSITIONS.includes(word.toLowerCase());

          if (isPreposition) {
            return String(word).toLowerCase();
          }

          return first.toUpperCase() + rest.join('').toLowerCase();
        })
        .join(' ');
    }

    return text;
  } catch (err) {
    return text;
  }
};

export const transformStringSingularIntoPlural = ({ data, value }: ITransformStringSingularIntoPluralProps) => {
  let valueString = '';

  value.forEach((quant, index) => {
    if (quant === 0) {
      return;
    }

    const newName =
      quant < 2 ? data[index].name.toLowerCase() : `${data[index].name.toLowerCase().replace(/ /, 's ')}s`;

    valueString += `${quant} ${newName}, `;
  });

  return valueString.slice(0, -2);
};

export const formatStringsToPlural = (label: string, index: number) => {
  return `${index} ${label
    .split(' ')
    .map((word) => {
      if (index !== 1) {
        if (word.includes('ível')) {
          return word.replace('ível', 'íveis');
        }

        if (word.includes('ção')) {
          return word.replace('ção', 'ções');
        }

        if (word.endsWith('r')) {
          return word.concat('es');
        }

        return word.concat('s');
      }

      return word;
    })
    .join(' ')}`;
};

// DATES FORMATTING
export const formatDate = (date: Moment | string | null, format: string) => {
  return moment(date).locale('pt-br').format(format);
};

export const formatDateCreatedAt = (createdAt: string) => {
  const momentCreatedAt = moment(createdAt);

  if (momentCreatedAt.isBefore()) {
    return `em ${momentCreatedAt.format('DD/MM/YYYY [às] HH[h]mm')}`;
  }

  return `hoje às ${momentCreatedAt.format('HH[h]mm')}`;
};

// CURRENCY FORMATTING
const { format: formatMiles } = new Intl.NumberFormat('pt-br');
const { format: formatMoney } = new Intl.NumberFormat('pt-br', {
  minimumFractionDigits: 2,
  currency: 'BRL',
  style: 'currency',
});
export { formatMoney, formatMiles };

export function removeMoneyMask(value = '', digits = true) {
  const clearValue = value
    .replace('R$', '')
    .trim()
    .replace(/[^\d]+/g, '');
  if (digits) {
    return Number(`${clearValue.slice(0, -2)}.${clearValue.slice(-2)}`);
  }
  return Number(clearValue);
}

export function addMoneyMask(value = 0, sign = true) {
  const onlyDigits = String(value)
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');
  return maskCurrency(Number(onlyDigits), sign);
}

export function maskCurrency(value = 0, sign = true) {
  const locale = 'pt-BR';
  const currency = 'BRL';
  if (sign) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

export function isEmptyString(value: string | string[]) {
  if (Array.isArray(value)) {
    const result = value.filter((text) => String(text).trim().length === 0);
    return result.length > 0;
  }

  return String(value).trim().length === 0;
}
